"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ForumService = void 0;
const service_1 = require("./service");
const forum_1 = require("../models/forum");
const forumMapper_1 = require("../utils/forumMapper");
const favoriteMapper_1 = require("../utils/favoriteMapper");
const mapper_1 = require("../utils/mapper");
class ForumService extends service_1.Service {
    constructor(transport, auth) {
        super(transport, auth);
    }
    /**
     * Get the index of all forums (categories and subforums).
     * Command: "fi" (FORUM_INDEX) - 26982
     */
    async getForums() {
        const response = await this.transport.send('fi');
        this.checkError(response, 'fi');
        return forumMapper_1.ForumMapper.mapForumIndex(response.data);
    }
    /**
     * Get topics in a forum.
     * Command: "fs" (FORUM_SEARCH/SECTION?) - 29555
     */
    async getTopics(forumId, offset = 0, limit = 20) {
        const response = await this.transport.send('fs', [forumId, offset, limit]);
        this.checkError(response, 'fs');
        return forumMapper_1.ForumMapper.mapForumFull(response.data);
    }
    /**
     * Jump to a post by id (`act=findpost&pid=` / snapback).
     * Code: "fj" (27238)
     * Android `AbstractC0112a.k`: [3, postId, -1, topicId|0].
     */
    async jumpToPost(topicId, postId) {
        return this.jumpToResult(topicId, 3, postId);
    }
    mapForumJumpResponse(data, requestTopicId) {
        const row = data;
        return {
            topicId: typeof row[0] === 'number' && row[0] > 0 ? row[0] : requestTopicId,
            offset: typeof row[1] === 'number' ? row[1] : 0,
            postId: typeof row[2] === 'number' ? row[2] : 0,
            filter: typeof row[3] === 'number' ? row[3] : 0,
        };
    }
    /**
     * Jump to unread / last / specific post.
     * Type 1|2: [type, topicId, -1, 0]. Type 3: [3, postId, -1, topicId|0].
     * Response: [topicId, offset, postId, filter].
     */
    async jumpToResult(topicId, type, postId = 0) {
        const params = type === 3
            ? [type, postId, -1, topicId > 0 ? topicId : 0]
            : [type, topicId, -1, 0];
        const response = await this.transport.send('fj', params);
        this.checkError(response, 'fj');
        return this.mapForumJumpResponse(response.data, topicId > 0 ? topicId : 0);
    }
    /**
     * Get posts for a topic.
     * Code: "fr" (FORUM_READ)
     * Android client sends [topicId, offset, limit].
     * Optional 4th arg showPostMode (filter) when non-zero.
     */
    async getTopicPosts(topicId, offset = 0, limit = 20, filter = 0) {
        const params = filter !== 0
            ? [topicId, offset, limit, filter]
            : [topicId, offset, limit];
        const response = await this.transport.send('fr', params);
        this.checkError(response, 'fr');
        return forumMapper_1.ForumMapper.mapTopicPosts(response.data);
    }
    /**
     * Get extended information about a forum (breadcrumb, rules, statistics, moderators).
     * Command: "fd" (FORUM_DATA) - 25702
     * @param forumId Forum ID
     */
    async getForumInfo(forumId) {
        const response = await this.transport.send('fd', [forumId]);
        this.checkError(response, 'fd');
        return forumMapper_1.ForumMapper.mapForumInfo(response.data);
    }
    /**
     * Get forum announcement content by link id.
     * Command: "fn" (FORUM_ANNOUNCEMENT) — response [title, content].
     */
    async getAnnouncement(announcementId) {
        const res = await this.transport.send('fn', [announcementId]);
        this.checkError(res, 'fn');
        return forumMapper_1.ForumMapper.mapAnnouncement(announcementId, res.data);
    }
    /**
     * Get moderation history for a topic or post.
     * Command: "fh" (FORUM_HISTORY) - 26726
     * @param type 0 = topic history, 1 = post history
     * @param id Topic ID or Post ID
     * @returns Array of history records: [timestamp, userId, userName, colorIndex, actionDescription]
     */
    async getHistory(type, id) {
        const res = await this.transport.send('fh', [type, id]);
        this.checkError(res, 'fh');
        const items = res.data[0] || [];
        return items.map((item) => {
            const row = item;
            return {
                timestamp: mapper_1.PdaMapper.safeIsoDate(row[0]),
                userId: row[1],
                userName: mapper_1.PdaMapper.cleanString(row[2]),
                colorIndex: row[3],
                description: mapper_1.PdaMapper.cleanString(row[4]),
            };
        });
    }
    /**
     * Get topic offset for the first unread message.
     * @param topicId Topic ID.
     */
    async jumpToUnread(topicId) {
        return this.jumpToResult(topicId, 1);
    }
    /**
     * Get topic offset for the last message.
     * @param topicId Topic ID.
     */
    async jumpToLast(topicId) {
        return this.jumpToResult(topicId, 2);
    }
    /**
     * Get list of users currently reading a topic or forum.
     * Command: "me" (ENTITY_MEMBERS) - 25965
     * @param type "topic" | "forum" | "idx"
     * @param id Entity ID
     */
    async getWhoIsReading(type, id) {
        const response = await this.transport.send('me', [type, id]);
        this.checkError(response, 'me');
        return forumMapper_1.ForumMapper.mapWhoIsReading(response.data);
    }
    /**
     * Get history of votes (karma) for a post.
     * Command: "fk" (FORUM_KARMA) - 27494
     * @param postId Post ID
     */
    async getKarmaHistory(postId) {
        const response = await this.transport.send('fk', [postId, 0]);
        this.checkError(response, 'fk');
        // Response: [requestId, status, items[]]
        const items = response.data[0] || [];
        return items.map(item => ({
            timestamp: mapper_1.PdaMapper.safeIsoDate(item[0]),
            userId: item[1],
            userName: item[2],
            value: item[3]
        }));
    }
    /**
     * Vote on post karma (+/−).
     * Command: "fk" (FORUM_KARMA) - 27494
     * @param postId Post ID
     * @param action 1 = minus, 2 = plus (0 = history via getKarmaHistory)
     */
    async votePostKarma(postId, action) {
        const response = await this.transport.send('fk', [postId, action]);
        this.checkError(response, 'fk');
        return response.status === 0;
    }
    /**
     * Create or edit a post.
     * Command: "fp" (FORUM_POST) - 28774
     * @param topicId Topic ID
     * @param text Post content (BBCode)
     * @param attachments Array of Attachment IDs (file IDs)
     * @param postId Post ID to edit (0 for new post)
     * @param flags Bitmask: +1 (Signature), +2 (Smileys). Default is 3 (both).
     *              NOTE: Server currently ignores these flags when editing (postId != 0).
     * @param reason Reason for edit (optional)
     * @returns Promise<number> Post ID
     */
    async addPost(topicId, text, attachments = [], postId = 0, flags = 3, reason = '') {
        // Warning: flags are sent but ignored by server during editing (only work for new posts)
        const response = await this.transport.send('fp', [topicId, postId, flags, text, attachments, reason]);
        this.checkError(response, 'fp');
        // response.data = [ status, topicId, postId, totalPosts ]
        return response.data[2];
    }
    /**
     * Server-side preview of a post (BBCode, attachments layout).
     * Command: "fp" (FORUM_POST) - 28774, PostID = -1 (new) or negative edit ID.
     * @param topicId Topic ID
     * @param text Post BBCode text
     * @param attachments Uploaded attachment IDs
     * @param editPostId Post ID when editing (0 for new reply)
     * @param flags Preview flags (official client uses 0)
     */
    async previewPost(topicId, text, attachments = [], editPostId = 0, flags = 0) {
        const postId = editPostId > 0 ? -editPostId : -1;
        const response = await this.transport.send('fp', [topicId, postId, flags, text, attachments, '']);
        if (response.status === 5) {
            throw new Error('Слишком много букв');
        }
        if (response.status === 6) {
            throw new Error('Этот пост уже был отправлен');
        }
        if (response.status === 8) {
            const msg = response.data?.[0];
            throw new Error(typeof msg === 'string' && msg.trim() ? msg : 'Ошибка предпросмотра');
        }
        this.checkError(response, 'fp');
        const attachmentsRaw = response.data[1];
        const mappedAttachments = Array.isArray(attachmentsRaw)
            ? attachmentsRaw.map((item) => {
                const row = item;
                return {
                    id: Number(row[0]) || 0,
                    type: Number(row[1]) || 0,
                    name: mapper_1.PdaMapper.cleanString(row[2]),
                    size: typeof row[3] === 'number' ? row[3] : undefined,
                };
            }).filter((item) => item.id > 0)
            : [];
        return {
            content: mapper_1.PdaMapper.cleanString(response.data[0]),
            attachments: mappedAttachments,
        };
    }
    /**
     * Get or submit a forum form (wizard).
     * Command: "fw" (FORUM_WRITE_FORM) - 30566
     * @param forumId Forum ID
     * @param topicId Topic ID (0 for new topic)
     * @param action 0 = Get structure, 1 = Preview, 2 = Submit
     * @param data Optional data for preview/submit
     */
    async getForumForm(forumId, topicId = 0, action = 0, data = []) {
        const response = await this.transport.send('fw', [forumId, topicId, action, data]);
        this.checkError(response, 'fw');
        return forumMapper_1.ForumMapper.mapForumForm(response.data);
    }
    /**
     * Load releaser / post template form for an existing topic.
     * Command: "fw" with forumId=0, topicId set.
     */
    async getPostTemplateForm(topicId, action = 0, data = []) {
        return this.getForumForm(0, topicId, action, data);
    }
    /**
     * Preview fw form (new topic or releaser post).
     * Command: "fw" with action=1.
     */
    async previewForumForm(forumId, topicId = 0, data = []) {
        const response = await this.transport.send('fw', [forumId, topicId, 1, data]);
        this.handleForumFormSubmitResponse(response, 'fw');
        return forumMapper_1.ForumMapper.mapTemplatePreview(response.data);
    }
    async previewPostTemplate(topicId, data) {
        return this.previewForumForm(0, topicId, data);
    }
    handleForumFormSubmitResponse(response, command) {
        if (response.status === 5) {
            const errors = response.data?.[0];
            if (Array.isArray(errors)) {
                const messages = errors.filter((e) => e && typeof e === 'string');
                throw new Error('Ошибки валидации:\n' + messages.join('\n'));
            }
            throw new Error('Ошибка валидации формы');
        }
        if (response.status === 4) {
            throw new Error('Сообщение отправлено на модерацию');
        }
        this.checkError(response, command);
    }
    /**
     * Submit a forum form to create a topic.
     * Command: "fw" with action=2
     * @returns topicId on success
     * @throws Error with validation messages on status 5
     */
    async submitForm(forumId, data) {
        const response = await this.transport.send('fw', [forumId, 0, 2, data]);
        this.handleForumFormSubmitResponse(response, 'fw');
        // Success: response.data = [topicId]
        return response.data[0];
    }
    /**
     * Submit releaser / post template form in an existing topic.
     * Command: "fw" with forumId=0, topicId set, action=2.
     */
    async submitPostTemplate(topicId, data) {
        const response = await this.transport.send('fw', [0, topicId, 2, data]);
        this.handleForumFormSubmitResponse(response, 'fw');
        const raw = response.data;
        if (!Array.isArray(raw)) {
            throw new Error('Некорректный ответ сервера');
        }
        const resultTopicId = raw[0];
        const offset = raw[1];
        const postId = raw[2];
        if (typeof postId !== 'number' || typeof resultTopicId !== 'number' || typeof offset !== 'number') {
            throw new Error('Некорректный ответ сервера');
        }
        return { postId, topicId: resultTopicId, offset };
    }
    /**
     * Get favorite topics.
     * Command: "mf" (FAVORITES_LIST) - 26221
     * @param offset Offset
     * @param limit Limit
     * @param flags Flags (e.g. 0 for all)
     */
    async getFavorites(offset = 0, limit = 20, flags = 0) {
        const response = await this.transport.send('mf', [offset, limit, flags]);
        this.checkError(response, 'mf');
        const mapped = favoriteMapper_1.FavoriteMapper.mapFavoritesResponse(response.data);
        return {
            count: mapped.totalCount,
            topics: mapped.items
                .filter((item) => item.type === 1)
                .map((item) => ({
                id: item.id,
                title: item.title,
                subtitle: item.description,
                bitmask: item.topicBitmask | (item.hasUnread ? 32 : 0),
                replies: item.repliesCount,
                lastPostTs: item.lastPostTs,
                lastUserId: item.authorId,
                lastUserName: item.lastPostAuthorName || item.authorName,
            })),
        };
    }
    /**
     * Jump to a specific position in a topic.
     * Command: "fj" (FORUM_JUMP) - 27238
     * @param type 1: First unread, 2: Last message, 3: Specific post
     * @param topicId Topic ID
     * @param postId Post ID (for type 3)
     * @returns Offset for the target position
     */
    async jumpTo(topicId, type, postId = 0) {
        const result = await this.jumpToResult(topicId, type, postId);
        return result.offset;
    }
    /**
     * Send a complaint/report for a forum post, QMS message, or article comment.
     * Command: "ta" (FORUM_COMPLAINT) - 29801 (ASCII "ta")
     * @param type 0 = forum post, 1 = QMS message, 2 = article comment
     * @param targetId ID of the target post, QMS message, or comment
     * @param text Complaint message
     */
    async sendReport(type, targetId, text) {
        const response = await this.transport.send('ta', [type, targetId, text]);
        let status = forum_1.ReportStatus.UNKNOWN_ERROR;
        let message = 'Неизвестная ошибка';
        const rawStatus = response.status;
        if (rawStatus === 0) {
            status = forum_1.ReportStatus.SUCCESS;
            message = 'Жалоба успешно отправлена';
        }
        else if (rawStatus === 2) {
            status = forum_1.ReportStatus.LIMIT_ERROR;
            message = 'Ошибка ограничений (жалоба на свой пост, повторная жалоба или нет прав)';
        }
        else if (rawStatus === 3) {
            status = forum_1.ReportStatus.NOT_FOUND;
            message = 'Целевой объект не найден';
        }
        else if (rawStatus === 4) {
            status = forum_1.ReportStatus.TOO_SHORT;
            message = 'Текст жалобы слишком короткий';
        }
        else {
            this.checkError(response, 'ta');
        }
        return {
            status,
            message,
            _raw: response.data
        };
    }
    /**
     * Set flags/state of topic(s) (Open/Close, Hide/Show, Pin/Unpin, Favorite, Mark read).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 11
     * @param topicIds Topic ID or array of Topic IDs
     * @param flagId Flag: 1=Pin, 2=Hidden, 4=Closed, 8=Favorite, 32=Mark read (V0.java / CLI 2026-06-10)
     * @param value Target value for flag (usually same as flagId when on, 0 when off)
     * @param reason Optional reason/comment
     */
    async setTopicFlags(topicIds, flagId, value, reason = '') {
        const ids = Array.isArray(topicIds) ? topicIds : [topicIds];
        const response = await this.transport.send('fm', [11, ids, flagId, value, reason]);
        this.checkError(response, 'fm');
        const count = response.data[0];
        return response.status === 0 && typeof count === 'number' && count >= ids.length;
    }
    /**
     * Combine topics into the last topic in the array.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 13
     * @param topicIds Topic IDs to merge; the last ID is the target topic
     */
    async combineTopics(topicIds) {
        if (topicIds.length < 2) {
            throw new Error('combineTopics requires at least 2 topic IDs');
        }
        const response = await this.transport.send('fm', [13, topicIds]);
        this.checkError(response, 'fm');
        const merged = response.data[0];
        const expected = topicIds.length - 1;
        return response.status === 0 && typeof merged === 'number' && merged >= expected;
    }
    /**
     * Delete topic(s).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 14
     * @param topicIds Topic ID or array of Topic IDs
     * @param reason Optional reason/comment
     */
    async deleteTopics(topicIds, reason = '') {
        const ids = Array.isArray(topicIds) ? topicIds : [topicIds];
        const response = await this.transport.send('fm', [14, ids, 0, 0, reason]);
        this.checkError(response, 'fm');
        const count = response.data[0];
        return response.status === 0 && typeof count === 'number' && count >= ids.length;
    }
    /**
     * Set flags/state of post(s) (Pin/Unpin, Hide/Show, Protect/Unprotect).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 1
     * @param postIds Post ID or array of Post IDs
     * @param flagId Flag: 1 = Hat (pinned in topic header), 2 = Visibility (Hidden/Visible), 2048 = Protected
     * @param value Value: for 1 — 1 = Pinned, 0 = Unpinned; for 2 — 2 = Hidden, 0 = Visible; for 2048 — 2048 = Protected, 0 = Unprotected
     * @param reason Optional reason/comment
     */
    async setPostFlags(postIds, flagId, value, reason = '') {
        const ids = Array.isArray(postIds) ? postIds : [postIds];
        const response = await this.transport.send('fm', [1, ids, flagId, value, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Поднять пост в шапку темы (ссылка `act=zmod&CODE=update-pinned`).
     * Command: "fm", ActionID = 6 — как Android при перехвате zmod-ссылки.
     */
    async pinPostToTopicHeader(postId) {
        const response = await this.transport.send('fm', [6, [postId], 0, 0, '']);
        this.checkError(response, 'fm');
        return response.status === 0;
    }
    /**
     * Move post(s) to another topic.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 2
     * @param postIds Post ID or array of Post IDs
     * @param targetTopicId Destination topic ID
     * @param reason Optional reason/comment
     */
    async movePosts(postIds, targetTopicId, reason = '') {
        const ids = Array.isArray(postIds) ? postIds : [postIds];
        const response = await this.transport.send('fm', [2, ids, targetTopicId, 0, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Combine posts into one (target author + timestamp).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 3
     * @param postIds Post IDs to merge
     * @param targetAuthorId Author ID of the resulting post
     * @param targetTimestamp Post unix timestamp (seconds)
     */
    async combinePosts(postIds, targetAuthorId, targetTimestamp, reason = '') {
        const response = await this.transport.send('fm', [3, postIds, targetAuthorId, targetTimestamp, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Delete post(s).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 4
     * @param postIds Post ID or array of Post IDs
     * @param reason Optional reason/comment
     */
    async deletePosts(postIds, reason = '') {
        const ids = Array.isArray(postIds) ? postIds : [postIds];
        const response = await this.transport.send('fm', [4, ids, 0, 0, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Restore post(s).
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 5
     * @param postIds Post ID or array of Post IDs
     * @param reason Optional reason/comment
     */
    async restorePosts(postIds, reason = '') {
        const ids = Array.isArray(postIds) ? postIds : [postIds];
        const response = await this.transport.send('fm', [5, ids, 0, 0, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Move topic(s) to another forum.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 12
     * @param topicIds Topic ID or array of Topic IDs
     * @param targetForumId Target Forum ID
     * @param leaveRedirect Whether to leave redirect link in original forum
     * @param reason Optional reason/comment
     */
    async moveTopics(topicIds, targetForumId, leaveRedirect = false, reason = '') {
        const ids = Array.isArray(topicIds) ? topicIds : [topicIds];
        const redirect = leaveRedirect ? 1 : 0;
        const response = await this.transport.send('fm', [12, ids, targetForumId, redirect, reason]);
        this.checkError(response, 'fm');
        const count = response.data[0];
        return response.status === 0 && typeof count === 'number' && count >= ids.length;
    }
    /**
     * Assign or remove curator for a topic.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 16
     * @param topicId Topic ID
     * @param userId User ID to assign as curator, or 0 to remove curator
     * @param reason Reason for modification
     */
    async modifyCurator(topicId, userId, reason = '') {
        const response = await this.transport.send('fm', [16, topicId, userId, 0, reason]);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Mark forum or subforums as read.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 21
     * @param forumIds Forum ID or array of Forum IDs to mark as read, or null/empty to mark all forums as read
     */
    async markForumsAsRead(forumIds) {
        const ids = forumIds === undefined || forumIds === null
            ? [-1]
            : (Array.isArray(forumIds) ? forumIds : [forumIds]);
        const response = await this.transport.send('fm', [21, ids, 32, 0, '']);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Subscribe / unsubscribe topic (избранное через fm 11, flag 8 — как в оф. клиенте).
     */
    async setTopicFavorite(topicIds, add) {
        return this.setTopicFlags(topicIds, 8, add ? 8 : 0);
    }
    /**
     * Subscribe / unsubscribe forum section (избранное через fm 21, flag 8 — как в оф. клиенте B0.java).
     */
    async setForumFavorite(forumIds, add) {
        const ids = Array.isArray(forumIds) ? forumIds : [forumIds];
        const response = await this.transport.send('fm', [21, ids, 8, add ? 8 : 0, '']);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
    /**
     * Vote in a topic poll.
     * Command: "fv" (30310, FORUM_VOTE — low byte first, как в оф. клиенте)
     * Request: [topicId, [[optionIndex, ...], ...]] — по одному массиву на вопрос.
     */
    async votePoll(topicId, selections) {
        const response = await this.transport.send('fv', [topicId, selections]);
        this.checkError(response, 'fv');
    }
    /**
     * Edit topic title, description and optional poll.
     * Command: "ft" (FORUM_TOPIC) - 29798
     * Request: [0, topicId, title, description, pollData?]
     */
    async editTopic(topicId, title, description, pollData = null) {
        const params = [0, topicId, title, description];
        if (pollData != null)
            params.push(pollData);
        const response = await this.transport.send('ft', params);
        this.checkError(response, 'ft');
    }
    /**
     * Mark topic(s) as read.
     * Command: "fm" (FORUM_MODIFY) - 28006, ActionID = 11
     * @param topicIds Topic ID or array of Topic IDs to mark as read
     */
    async markTopicsAsRead(topicIds) {
        const ids = Array.isArray(topicIds) ? topicIds : [topicIds];
        const response = await this.transport.send('fm', [11, ids, 32, 0, '']);
        this.checkError(response, 'fm');
        return response.status === 0 && response.data[0] === 1;
    }
}
exports.ForumService = ForumService;
